import React, { useRef, useMemo, useState, useCallback } from 'react'
import { useDevice } from 'device'
import logger from 'logger'

import s from './YoutubePlayer.scss'


declare global {
  interface Window {
    onYouTubeIframeAPIReady(): void
  }
}

const youtubeIframeAPIState = {
  isScriptInserted: false,
  isReady: false,
  onReadyWaitList: [],
}

const initApi = (callback) => {
  if (youtubeIframeAPIState.isReady) {
    callback()
    return
  }

  youtubeIframeAPIState.onReadyWaitList.push(callback)

  if (youtubeIframeAPIState.isScriptInserted) {
    return
  }

  const tag = document.createElement('script')
  tag.src = 'https://www.youtube.com/iframe_api'

  const firstScriptTag = document.getElementsByTagName('script')[0]
  firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)

  youtubeIframeAPIState.isScriptInserted = true

  window.onYouTubeIframeAPIReady = () => {
    youtubeIframeAPIState.isReady = true
    youtubeIframeAPIState.onReadyWaitList.forEach((callback) => callback())
  }
}

type YoutubePlayerProps = {
  url: string
  preview?: string
  autoplay?: boolean
  onPlay?: () => void
}

const YoutubePlayer: React.FC<YoutubePlayerProps> = (props) => {
  const { url, preview, autoplay, onPlay } = props

  const { isMobile } = useDevice()
  const ref = useRef<HTMLIFrameElement>()

  const withPreview = Boolean(preview && preview !== url)

  const src = useMemo(() => {
    if (autoplay || withPreview) {
      const hasQuery = /\?[^?]+$/.test(url)

      return `${url}${hasQuery ? '&' : '?'}autoplay=1&enablejsapi=1`
    }

    return url
  }, [ url, autoplay, withPreview ])

  const [ isVideoVisible, setVideoVisibility ] = useState(!withPreview)

  const handlePlaceholderClick = useCallback(() => {
    setVideoVisibility(true)

    if (typeof onPlay === 'function') {
      onPlay()
    }

    if (isMobile) {
      // it's a big fallback for mobile devices
      // autoplay isn't working in mobile browsers, so we should start video manually by API
      // to do so, we should load iframe api
      initApi(() => {
        // we need setTimeout, because iframe should be rendered after state update
        setTimeout(() => {
          try {
            // @ts-ignore
            const player = new window.YT.Player(ref.current, {
              events: {
                'onReady': () => {
                  player.playVideo()
                },
              },
            })
          }
          catch (error) {
            logger.error(error)
          }
        }, 50)
      })
    }
  }, [ isMobile, ref, onPlay ])

  return (
    <>
      {
        Boolean(withPreview) && (
          <div
            className={s.preview}
            role="button"
            tabIndex={0}
            style={{ backgroundImage: `url(${preview})` }}
            onClick={handlePlaceholderClick}
          >
            <div className={s.icon} />
          </div>
        )
      }
      {
        isVideoVisible && (
          <iframe
            ref={ref}
            className={s.video}
            src={src}
            title="youtube video"
            allow="autoplay"
            frameBorder="0"
            allowFullScreen
          />
        )
      }
    </>
  )
}


export default YoutubePlayer
